import { Box, Grid, InternalGrid, ThemeDefault } from '@paygreen/paygreen-ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import {
    H1Title,
    ModalIcon,
    NotAvailableForTestMessage,
    ToggleMode,
} from '../../../components';
import { useAPIError, useMode, useUser } from '../../../context';
import { MODES } from '../../../utils/constants';
import dayjs from '../../../utils/dayjsHelper';
import { useNodeDimensions } from '../../../utils/hooks';
import { IsDesktop } from '../../../utils/tools';
import { getCarbonReports } from '../../emissions/service';
import ContributionsExplanationModal from './ContributionsExplanationModal';
import ContributionsGlobalCard from './ContributionsGlobalCard';

const ContributionsGlobalView = ({ t }) => {
    const { user } = useUser();
    const { mode } = useMode();
    const { addError } = useAPIError();

    const [reportsData, setReportsData] = useState();
    const [isLoading, setIsLoading] = useState(false);

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    // we will request all carbon offset price since the creation of the user inside API
    const userCreationDate =
        user.createdAt &&
        dayjs(new Date(user.createdAt * 1000)).format('YYYY-MM-DD');

    // we create a ref using a special hook to get width from main Grid component
    const [ref, { width }] = useNodeDimensions();

    const fetchGlobalContributionsData = useCallback(() => {
        isMounted.current && setIsLoading(true);

        const params = {
            begin: userCreationDate,
        };

        getCarbonReports(params, mode)
            .then((res) => {
                if (isMounted.current) {
                    setReportsData(res?.data || null);
                    setIsLoading(false);
                }
            })
            .catch((error) => {
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                );
            });
    }, [addError, userCreationDate, mode]);

    useEffect(() => {
        isMounted.current = true;

        fetchGlobalContributionsData();

        return () => {
            isMounted.current = false;
        };
    }, [fetchGlobalContributionsData]);

    return (
        <>
            <Helmet>
                <title>
                    {t('contributions.globalView.metaTitle')} -{' '}
                    {t('main.metaTitle')}
                </title>
            </Helmet>

            <InternalGrid
                displayType="grid"
                alignItems="flex-start"
                justifyItems="start"
                hasStaticWidth={false}
                gridTemplateColumns="auto"
                gridGap="xs"
            >
                <H1Title
                    marginTop={IsDesktop() ? 'none' : 'xs'}
                    title={t('contributions.globalView.title')}
                    infoIcon={
                        <ModalIcon
                            title={t('dashboard.equivalence.modal.title')}
                            icon="QuestionBoldIcon"
                            iconSize="sm"
                        >
                            <ContributionsExplanationModal />
                        </ModalIcon>
                    }
                >
                    <ToggleMode />
                </H1Title>

                {mode === MODES.TEST && <NotAvailableForTestMessage />}
            </InternalGrid>

            <Box marginTop={mode === MODES.TEST ? 'lg' : 'sm'}>
                <div ref={ref}>
                    <Grid
                        displayType="grid"
                        gridTemplateColumns={
                            width > ThemeDefault.breakpoint.md
                                ? '2fr 2fr'
                                : '100%'
                        }
                        gridGap="lg"
                        justifyItems="stretch"
                        childrenMargin="md"
                        childrenMarginBig="none"
                        blockPadding="none"
                        align="left"
                    >
                        <ContributionsGlobalCard
                            reportsData={reportsData}
                            isLoading={isLoading}
                        />
                    </Grid>
                </div>
            </Box>
        </>
    );
};

export default withTranslation('common')(ContributionsGlobalView);
